import { motion, useInView, useAnimate, stagger } from 'framer-motion';
import { useContext, useEffect } from 'react';
import { PageContext } from '../../contexts';
import { useInViewScroll, useSanitizedTranslation } from '../../hooks';
import { CTA, IImage } from '../../types';
import { IFormField, XumoForm } from 'ui';
import { useScrolledTheme } from 'ui';
import Image from 'next/image';

export interface AboutFormProps {
  key?: string;
  heading: string;
  body: string;
  formFields: IFormField[];
  CTA: CTA;
  backgroundColor?: string;
  bottomBackgroundColor?: string;
  backgroundImage?: IImage;
  headingOne?: boolean;
  styleOverrides?: {
    form?: string;
    formField?: string;
    CTA?: {
      general?: string;
      disabledColor?: string;
      enabledColor?: string;
      alignment?: string;
    };
    hideLabel?: boolean;
  };
}

const squiggleVariant = {
  hidden: {
    opacity: 0,
    scale: 1.05,
    transition: {
      when: 'afterChildren',
      delayChildren: 0,
      staggerChildren: 0.025,
      type: 'spring',
      stiffness: 400,
      damping: 40,
    },
  },
  show: {
    opacity: 1,
    scale: 1,
    transition: {
      staggerChildren: 0.055,
      type: 'spring',
      stiffness: 90,
      damping: 23,
      restDelta: 0.001,
    },
  },
};

export function AboutForm({ CTA, heading, body, formFields, backgroundColor, headingOne, styleOverrides }: AboutFormProps) {
  const { ref, progress: y } = useInViewScroll([200, 0], ['start 100vh', 'end 0vh']);
  const { pageCtx } = useContext(PageContext);
  const { t } = useSanitizedTranslation();

  const { host } = pageCtx;

  const HeadingComponent = headingOne ? motion.h1 : motion.h2;

  const { componentInView } = useScrolledTheme({
    ref,
    background: backgroundColor,
    // TODO: add accent to directus
    accent: '#000000',
    text: '#000000',
  });

  const [viewref, animate] = useAnimate();
  const inView = useInView(ref, { once: true, margin: '-30% 0% -30% 0%' });

  useEffect(() => {
    if (inView) {
      animate(
        '.formwrapper',
        {
          y: [50, 0],
          opacity: [0, 1],
        },
        {
          ease: [0, 1, 1, 1],
          duration: 0.9,
          deltaRest: 0.000001,
          deltaSpeed: 0.000001,
        },
      );
    }
  }, [inView]);

  return (
    <>
      <div ref={ref} className="relative z-40 grid w-full grid-cols-1 grid-rows-1 items-center justify-center bg-[#EFF3F5] py-32">
        {/* Form */}
        <section className="wrapper relative z-0 col-start-1 row-start-1 w-full" data-testid={'lead-form-section'}>
          {/* Anchor */}
          <div id="contact" className="absolute -top-[15vh]"></div>

          {/* Form wrapper */}
          <div className="relative flex w-full flex-col items-center lg:px-28" ref={viewref}>
            <div
              className="form.modal content-wrapper grow-1 formwrapper z-20 flex w-full flex-col
        items-center justify-center rounded-lg bg-white"
            >
              <HeadingComponent
                className="type-heading mb-8 px-12 text-center md:mb-8 md:px-0"
                data-i18n={heading}
                dangerouslySetInnerHTML={{ __html: t(heading) }}
              />
              <div className="type-body allow-html -mb-6 p-6 text-center lg:w-[65%]" data-i18n={body} dangerouslySetInnerHTML={{ __html: t(body) }} />
              <div className="w-full lg:w-2/3">
                <XumoForm
                  formFields={formFields}
                  CTA={CTA}
                  formAction={{
                    submissionEndpoint: '/api/prospect',
                    successMessage: 'We’ve received your info — our emails will be landing in your inbox soon.',
                  }}
                  styleOverrides={{
                    CTA: {
                      general: `text-white border-transparent py-2 px-12`,
                      disabledColor: 'bg-xumoAltBerryDark',
                      enabledColor: 'bg-xumoAltBerryLight',
                    },
                    formField: `w-full`,
                    form: 'w-full justify-center items-center px-8 sm:px-16 gap-4',
                    ...styleOverrides,
                  }}
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
